/**
 * Groups events by date and calculates row spans, sorting events by time within each date.
 * For events with the same start time, sorts by event_number (if available) or id.
 *
 * @param {Array} events - The array of event objects.
 * @returns {Array} - Array of grouped events with date and rowSpan.
 */
export function groupEventsByDate(events) {
  if (!Array.isArray(events)) return [];

  // Group events by start_date
  const groupedEvents = events?.reduce((acc, event) => {
    const date = event.start_date; // Use start_date field for grouping
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});

  // Convert the grouped object into an array of objects with date and events, and sort each group by start_time
  const groupedArray = Object.entries(groupedEvents)?.map(([date, events]) => ({
    date,
    events: events?.sort((a, b) => {
      // Primary sort: by start_at timestamp
      const timeCompare = new Date(a.start_at) - new Date(b.start_at);
      if (timeCompare !== 0) return timeCompare;
      
      // Secondary sort: by event_number if both events have it
      if (a.event_number != null && b.event_number != null) {
        const aNum = Number(a.event_number);
        const bNum = Number(b.event_number);
        // If both are valid numbers, compare them
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
      }
      
      // Tertiary sort: by id if event_number comparison is not applicable
      return a.id - b.id;
    }),
    rowSpan: events.length,
  }));

  // Sort the grouped array by date
  groupedArray.sort((a, b) => new Date(a.date) - new Date(b.date));

  return groupedArray;
}
